export type ProseMirrorMark = {
  type: string;
  attrs?: Record<string, unknown>;
};

export type ProseMirrorNode = {
  type?: string;
  text?: string;
  marks?: ProseMirrorMark[];
  attrs?: Record<string, unknown>;
  content?: ProseMirrorNode[];
};

const MARK_ALIASES: Record<string, string> = {
  strong: 'bold',
  em: 'italic',
};

const YOUTUBE_HOSTS = ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtu.be', 'www.youtu.be', 'youtube-nocookie.com', 'www.youtube-nocookie.com'];

const YOUTUBE_ID_PATTERN = /^[\w-]{6,20}$/;

export const DYNAMIC_PAGE_SUB_TYPE = 'SUBTYPE_DYNAMIC_PAGE';
export const DYNAMIC_EMBED_SUB_TYPE = 'SUBTYPE_DYNAMIC_EMBED';
export const DOCUMENT_PDF_SUB_TYPE = 'SUBTYPE_DOCUMENT_PDF';
export const ASSIGNMENT_TYPE = 'TYPE_ASSIGNMENT';
export const ASSIGNMENT_SUB_TYPE = 'SUBTYPE_ASSIGNMENT_ANY';

export function isLearnHouseAssignmentActivity(activity: { activity_type?: string; activity_sub_type?: string }) {
  return activity.activity_type === ASSIGNMENT_TYPE || activity.activity_sub_type === ASSIGNMENT_SUB_TYPE;
}

export type LearnHousePdfContent = {
  filename: string;
  activity_uuid?: string;
};

export type LearnHouseEmbedContent = {
  embed_url: string;
};

export function parseLearnHousePdfContent(content: unknown): LearnHousePdfContent | null {
  if (content === null || content === undefined || typeof content !== 'object' || Array.isArray(content)) {
    return null;
  }

  const filename = (content as { filename?: unknown }).filename;
  if (typeof filename !== 'string' || filename.trim() === '' || /[/\\:]/.test(filename) || filename.includes('..')) {
    return null;
  }

  const activityUuid = (content as { activity_uuid?: unknown }).activity_uuid;
  return {
    filename,
    activity_uuid: typeof activityUuid === 'string' ? activityUuid : undefined,
  };
}

export function normalizeMarkTypes(node: ProseMirrorNode): ProseMirrorNode {
  const normalized: ProseMirrorNode = { ...node };

  if (Array.isArray(node.marks)) {
    normalized.marks = node.marks.map((mark) => (MARK_ALIASES[mark.type] ? { ...mark, type: MARK_ALIASES[mark.type] } : mark));
  }

  if (Array.isArray(node.content)) {
    normalized.content = node.content.map(normalizeMarkTypes);
  }

  return normalized;
}

export function parseLearnHouseContent(content: unknown): ProseMirrorNode | null {
  if (content === null || content === undefined) {
    return null;
  }

  let parsed: unknown = content;

  if (typeof content === 'string') {
    if (content.trim() === '') {
      return null;
    }

    try {
      parsed = JSON.parse(content);
    } catch {
      return null;
    }
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return null;
  }

  const node = parsed as ProseMirrorNode;
  if (node.type !== 'doc') {
    return null;
  }

  return normalizeMarkTypes(node);
}

export function isDocumentEmpty(doc: ProseMirrorNode | null): boolean {
  if (!doc) {
    return true;
  }

  const children = doc.content ?? [];
  if (children.length === 0) {
    return true;
  }

  return children.every((child) => child.type === 'paragraph' && (child.content ?? []).length === 0);
}

export function getSafeHref(href: unknown): string | null {
  if (typeof href !== 'string' || href.trim() === '') {
    return null;
  }

  try {
    const url = new URL(href, window.location.origin);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

export function getYouTubeEmbedUrl(source: unknown): string | null {
  if (typeof source !== 'string' || source.trim() === '') {
    return null;
  }

  const value = source.trim();

  if (YOUTUBE_ID_PATTERN.test(value) && !value.includes('.')) {
    return `https://www.youtube.com/embed/${value}`;
  }

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return null;
  }

  if (!YOUTUBE_HOSTS.includes(url.hostname.toLowerCase())) {
    return null;
  }

  const videoId = url.hostname.toLowerCase().includes('youtu.be') ? url.pathname.slice(1) : (url.searchParams.get('v') ?? url.pathname.replace(/^\/(embed|v)\//, ''));

  if (!videoId || !YOUTUBE_ID_PATTERN.test(videoId)) {
    return null;
  }

  return `https://www.youtube.com/embed/${videoId}`;
}

export const EDITABLE_LEARN_HOUSE_NODE_TYPES = new Set([
  'doc',
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'listItem',
  'blockquote',
  'codeBlock',
  'hardBreak',
  'horizontalRule',
  'text',
]);

export const PRESERVED_MEDIA_NODE_TYPES = new Set(['blockImage', 'blockPDF']);

export const LEARN_HOUSE_FROZEN_NODE_TYPE = 'lhFrozen';

function walkNodes(node: ProseMirrorNode, visit: (node: ProseMirrorNode) => void) {
  visit(node);
  for (const child of node.content ?? []) {
    walkNodes(child, visit);
  }
}

export function mediaBlockSignature(node: ProseMirrorNode): string {
  const attrs = node.attrs ?? {};
  const blockObject = attrs.blockObject && typeof attrs.blockObject === 'object' && !Array.isArray(attrs.blockObject) ? (attrs.blockObject as Record<string, unknown>) : null;
  const content = blockObject?.content && typeof blockObject.content === 'object' && !Array.isArray(blockObject.content) ? (blockObject.content as Record<string, unknown>) : null;
  const identity = content?.file_id ?? blockObject?.block_uuid ?? JSON.stringify(attrs);
  return `${node.type ?? 'unknown'}:${String(identity)}`;
}

export function collectMediaBlockSignatures(doc: ProseMirrorNode): string[] {
  const signatures: string[] = [];
  walkNodes(doc, (node) => {
    if (node.type && PRESERVED_MEDIA_NODE_TYPES.has(node.type)) {
      signatures.push(mediaBlockSignature(node));
    }
  });
  return signatures;
}

export function missingMediaBlocks(original: ProseMirrorNode, next: ProseMirrorNode): string[] {
  const remaining = new Set(collectMediaBlockSignatures(next));
  return collectMediaBlockSignatures(original).filter((signature) => !remaining.has(signature));
}

export function toEditorDocument(node: ProseMirrorNode): ProseMirrorNode {
  if (node.type === 'text' || !node.type) {
    return node;
  }

  if (!EDITABLE_LEARN_HOUSE_NODE_TYPES.has(node.type)) {
    return {
      type: LEARN_HOUSE_FROZEN_NODE_TYPE,
      attrs: { payload: node },
    };
  }

  return {
    ...node,
    content: node.content?.map(toEditorDocument),
  };
}

export function fromEditorDocument(node: ProseMirrorNode): ProseMirrorNode {
  if (node.type === LEARN_HOUSE_FROZEN_NODE_TYPE) {
    const payload = node.attrs?.payload;
    if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
      return payload as ProseMirrorNode;
    }

    return { type: 'paragraph' };
  }

  if (node.type === 'text' || !node.type) {
    return node;
  }

  return {
    ...node,
    content: node.content?.map(fromEditorDocument),
  };
}

export function parseLearnHouseEmbedContent(content: unknown): LearnHouseEmbedContent | null {
  if (content === null || content === undefined || typeof content !== 'object' || Array.isArray(content)) {
    return null;
  }

  const embedUrl = (content as { embed_url?: unknown }).embed_url;
  if (typeof embedUrl !== 'string' || !getYouTubeEmbedUrl(embedUrl)) {
    return null;
  }

  return { embed_url: embedUrl };
}
